import { useEffect } from "react";
import { useAppStore } from "@/store";
import { NotesViewPageStore } from "@/store/pages/NotesViewPageStore/NotesViewPageStore";
import { useMaybeTabContext } from "@/components/layout/tab-context";
import { MemCommonEditorStore } from "@/components/note/store/mem-common-editor-store";

/**
 * Hook that syncs note version history state with the editor's diff display.
 * Observes changes in the version history store and updates the editor accordingly.
 */
export function useNoteVersionHistory(editorStore: MemCommonEditorStore | undefined) {
  const { pageStore } = useAppStore();
  const maybeTab = useMaybeTabContext();
  const notesViewPageStore = maybeTab
    ? (pageStore.getPageStore(
        maybeTab.activeTab,
        maybeTab.activeTabPath,
        "notesViewPage"
      ) as NotesViewPageStore)
    : undefined;
  const noteVersionHistory = notesViewPageStore?.noteVersionHistory;

  // Sync version history state with editor diff display
  useEffect(() => {
    if (!editorStore) return;
    if (!noteVersionHistory) return;
    if (noteVersionHistory.isLoading) return;

    const editorNoteId = editorStore.noteObservable?.id;
    const historyNoteId = noteVersionHistory.currentNoteId;
    // Only apply diffs when the history is for this editor's note
    if (!editorNoteId || !historyNoteId || historyNoteId !== editorNoteId) {
      editorStore.closeDifferences();
      return;
    }

    if (noteVersionHistory.shouldShowDifferences && noteVersionHistory.versionForDifferences) {
      editorStore.showDifferencesBetweenVersions({
        finalVersion: noteVersionHistory.versionForDifferences,
        baseVersion: noteVersionHistory.baseVersionForDifferences || undefined,
      });

      return;
    }

    if (!noteVersionHistory.shouldShowDifferences) {
      editorStore.closeDifferences();
      return;
    }
  }, [
    editorStore,
    noteVersionHistory,
    noteVersionHistory?.shouldShowDifferences,
    noteVersionHistory?.versionForDifferences,
    noteVersionHistory?.baseVersionForDifferences,
    noteVersionHistory?.isLoading,
  ]);
}
